import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, Skeleton, useTheme, Grid } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

interface DeviceData {
  device_type: string;
  count: number;
}

interface BrowserData {
  browser: string;
  count: number;
}

interface OSData {
  os: string;
  count: number;
}

interface DevicesData {
  devices: DeviceData[];
  browsers: BrowserData[];
  operating_systems: OSData[];
}

interface DevicesChartProps {
  data: DevicesData | null;
  loading: boolean;
}

const DEVICE_COLORS = ['#2196f3', '#4caf50', '#ff9800'];
const BROWSER_COLORS = ['#e91e63', '#9c27b0', '#673ab7', '#3f51b5', '#00bcd4', '#009688', '#795548'];
const OS_COLORS = ['#f44336', '#ff5722', '#ffc107', '#8bc34a', '#00bcd4', '#607d8b', '#9e9e9e'];

interface MiniPieChartProps {
  title: string;
  data: Array<{ name: string; value: number }>;
  colors: string[];
  loading: boolean;
}

const MiniPieChart: React.FC<MiniPieChartProps> = ({ title, data, colors, loading }) => {
  const theme = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Use double rAF to ensure DOM is fully laid out before rendering chart
    let raf2: number;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        setMounted(true);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, []);

  if (loading || !mounted) {
    return (
      <Box>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          {title}
        </Typography>
        <Skeleton variant="circular" width={120} height={120} />
      </Box>
    );
  }

  if (data.length === 0) {
    return (
      <Box>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No data
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Box sx={{ width: '100%', minWidth: 0 }}>
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={30}
              outerRadius={60}
              paddingAngle={2}
              dataKey="value"
            >
              {data.map((_, index) => (
                <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 4,
              }}
              formatter={(value: number, name: string) => [`${value} (${name})`, '']}
            />
            <Legend
              verticalAlign="bottom"
              height={36}
              formatter={(value) => (
                <span style={{ color: theme.palette.text.primary, fontSize: 11 }}>{value}</span>
              )}
            />
          </PieChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

const DevicesChart: React.FC<DevicesChartProps> = ({ data, loading }) => {
  const deviceData = data?.devices?.map((d) => ({
    name: d.device_type.charAt(0).toUpperCase() + d.device_type.slice(1),
    value: d.count,
  })) || [];

  const browserData = data?.browsers?.slice(0, 5).map((b) => ({
    name: b.browser,
    value: b.count,
  })) || [];

  const osData = data?.operating_systems?.slice(0, 5).map((o) => ({
    name: o.os,
    value: o.count,
  })) || [];

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Devices & Browsers
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <MiniPieChart
              title="Device Type"
              data={deviceData}
              colors={DEVICE_COLORS}
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <MiniPieChart
              title="Browser"
              data={browserData}
              colors={BROWSER_COLORS}
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <MiniPieChart
              title="Operating System"
              data={osData}
              colors={OS_COLORS}
              loading={loading}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default DevicesChart;
